import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  DialogContentText,
  Tooltip,
  TableSortLabel,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import { fetchDomains, addDomain, updateDomain, deleteDomain, fetchDomainAccess } from '../store/slices/domainsSlice';
import DomainSettingsDialog from '../components/DomainSettingsDialog';

const Domains = () => {
  const dispatch = useDispatch();
  const { domains, loading, error } = useSelector((state) => state.domains);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [domainToDelete, setDomainToDelete] = useState(null);
  const [accessDialogOpen, setAccessDialogOpen] = useState(false);
  const [selectedDomainForAccess, setSelectedDomainForAccess] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    field: 'createdAt',
    order: 'desc'
  });

  useEffect(() => {
    dispatch(fetchDomains({ sortBy: sortConfig.field, sortOrder: sortConfig.order }));
  }, [dispatch, sortConfig]);

  const handleAddClick = () => {
    setAddDialogOpen(true);
  };

  const handleAddClose = () => {
    setAddDialogOpen(false);
  };

  const handleAddSave = async (formData) => {
    try {
      await dispatch(addDomain(formData)).unwrap();
      handleAddClose();
    } catch (error) {
      console.error('Failed to add domain:', error);
    }
  };

  const handleEditClick = (domain) => {
    setSelectedDomain(domain);
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setSelectedDomain(null);
  };

  const handleEditSave = async (formData) => {
    try {
      await dispatch(updateDomain(formData)).unwrap();
      handleEditClose();
    } catch (error) {
      console.error('Failed to update domain:', error);
    }
  };

  const handleDeleteClick = (domain) => {
    setDomainToDelete(domain);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await dispatch(deleteDomain(domainToDelete._id)).unwrap();
      setDeleteDialogOpen(false);
      setDomainToDelete(null);
    } catch (error) {
      console.error('Failed to delete domain:', error);
    }
  };

  const handleDeleteClose = () => {
    setDeleteDialogOpen(false);
    setDomainToDelete(null);
  };

  const handleAccessClick = async (domain) => {
    setSelectedDomainForAccess(domain);
    setAccessDialogOpen(true);
    await dispatch(fetchDomainAccess(domain._id));
  };

  const handleAccessClose = () => {
    setAccessDialogOpen(false);
    setSelectedDomainForAccess(null);
  };

  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      order: prev.field === field && prev.order === 'asc' ? 'desc' : 'asc'
    }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: 3,
      width: '100%',
      maxWidth: '100%',
      '& .MuiContainer-root': {
        maxWidth: '100%',
        width: '100%',
        padding: 0
      }
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Domains
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
        >
          Add Domain
        </Button>
      </Box>
      <TableContainer 
        component={Paper} 
        sx={{ 
          maxHeight: 'calc(100vh - 200px)',
          width: '100%',
          '& .MuiTableCell-root': {
            padding: '12px 16px'
          }
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortConfig.field === 'domain'}
                  direction={sortConfig.field === 'domain' ? sortConfig.order : 'asc'}
                  onClick={() => handleSort('domain')}
                >
                  Domain
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortConfig.field === 'url'}
                  direction={sortConfig.field === 'url' ? sortConfig.order : 'asc'}
                  onClick={() => handleSort('url')}
                >
                  URL
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortConfig.field === 'redirectUrl'}
                  direction={sortConfig.field === 'redirectUrl' ? sortConfig.order : 'asc'}
                  onClick={() => handleSort('redirectUrl')}
                >
                  Redirect URL
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortConfig.field === 'redirectPercentage'}
                  direction={sortConfig.field === 'redirectPercentage' ? sortConfig.order : 'asc'}
                  onClick={() => handleSort('redirectPercentage')}
                >
                  Redirect %
                </TableSortLabel>
              </TableCell>
              <TableCell>Device Types</TableCell>
              <TableCell>Target Countries</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortConfig.field === 'createdAt'}
                  direction={sortConfig.field === 'createdAt' ? sortConfig.order : 'asc'}
                  onClick={() => handleSort('createdAt')}
                >
                  Created At
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortConfig.field === 'lastDetected'}
                  direction={sortConfig.field === 'lastDetected' ? sortConfig.order : 'asc'}
                  onClick={() => handleSort('lastDetected')}
                >
                  Last Detected
                </TableSortLabel>
              </TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {domains.map((domain) => (
              <TableRow 
                key={domain._id}
                hover
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell 
                  sx={{ 
                    fontWeight: 'medium',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                  title={domain.domain}
                >
                  {domain.domain}
                </TableCell>
                <TableCell 
                  sx={{ 
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                  title={domain.url}
                >
                  {domain.url}
                </TableCell>
                <TableCell 
                  sx={{ 
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                  title={domain.redirectUrl}
                >
                  {domain.redirectUrl}
                </TableCell>
                <TableCell>{domain.redirectPercentage}%</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {domain.deviceTypes.map((type) => (
                      <Chip 
                        key={type} 
                        label={type} 
                        size="small"
                        sx={{ 
                          height: '24px',
                          '& .MuiChip-label': { px: 1 }
                        }}
                      />
                    ))}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      flexWrap: 'wrap', 
                      gap: 0.25,
                      lineHeight: 1
                    }}
                  >
                    {domain.targetCountries.map((country) => (
                      <Chip 
                        key={country} 
                        label={country} 
                        size="small"
                        sx={{ 
                          height: '20px',
                          '& .MuiChip-label': { 
                            px: 0.5,
                            fontSize: '0.7rem',
                            lineHeight: 1.2
                          }
                        }}
                      />
                    ))}
                  </Box>
                </TableCell>
                <TableCell>
                  {new Date(domain.createdAt).toLocaleString()}
                </TableCell>
                <TableCell>
                  {new Date(domain.lastDetected).toLocaleString()}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Show Statistics">
                      <IconButton 
                        onClick={() => handleAccessClick(domain)}
                        size="small"
                        sx={{ 
                          color: 'info.main',
                          '&:hover': { bgcolor: 'info.light' }
                        }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit Domain">
                      <IconButton 
                        onClick={() => handleEditClick(domain)}
                        size="small"
                        sx={{ 
                          color: 'primary.main',
                          '&:hover': { bgcolor: 'primary.light' }
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Domain">
                      <IconButton 
                        onClick={() => handleDeleteClick(domain)}
                        size="small"
                        sx={{ 
                          color: 'error.main',
                          '&:hover': { bgcolor: 'error.light' }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <DomainSettingsDialog
        open={editDialogOpen}
        onClose={handleEditClose}
        domain={selectedDomain}
        onSave={handleEditSave}
      />

      <DomainSettingsDialog
        open={addDialogOpen}
        onClose={handleAddClose}
        onSave={handleAddSave}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteClose}
      >
        <DialogTitle>Delete Domain</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {domainToDelete?.domain}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteClose}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <DomainAccessDialog
        open={accessDialogOpen}
        onClose={handleAccessClose}
        domain={selectedDomainForAccess}
      />
    </Box>
  );
};

const DomainAccessDialog = ({ open, onClose, domain }) => {
  const { accessList, accessLoading, accessError } = useSelector((state) => state.domains);
  const domainAccessList = domain ? accessList[domain._id] || [] : [];

  if (accessLoading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Domain Access List</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (accessError) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Domain Access List</DialogTitle>
        <DialogContent>
          <Alert severity="error">{accessError}</Alert>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="false" fullWidth>
      <DialogTitle>Domain Access List</DialogTitle>
      <DialogContent>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>IP</TableCell>
                <TableCell>User Agent</TableCell>
                <TableCell>Device Type</TableCell>
                <TableCell>Country</TableCell>
                <TableCell>Timestamp</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {domainAccessList.map((access) => (
                <TableRow key={access._id}>
                  <TableCell>{access.ip}</TableCell>
                  <TableCell>{access.userAgent}</TableCell>
                  <TableCell>{access.deviceType}</TableCell>
                  <TableCell>{access.country}</TableCell>
                  <TableCell>{new Date(access.createdAt).toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default Domains; 